import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import Layout from "@/components/Layout";
import { apiGet, apiPost, apiDelete, apiPatch } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Plus, Trash2, ExternalLink, Sparkles, Upload, Loader2 } from "lucide-react";
import { toast } from "sonner";

const RELATIONSHIP_PRESETS = [
  "prospect",
  "peer",
  "client",
  "big creator",
  "industry expert",
  "partner",
  "customer",
];

export default function Contacts() {
  const { workspaceId } = useParams();
  return (
    <Layout
      title="Contacts"
    >
      <ContactsPanel workspaceId={workspaceId} />
    </Layout>
  );
}

export function ContactsPanel({ workspaceId }) {
  const [items, setItems] = useState([]);
  const [voiceProfiles, setVoiceProfiles] = useState([]);
  const [open, setOpen] = useState(false);
  const [bulkOpen, setBulkOpen] = useState(false);

  const load = async () => {
    const cs = await apiGet(`/workspaces/${workspaceId}/contacts`);
    setItems(cs);
    try {
      const vps = await apiGet(`/workspaces/${workspaceId}/voice_profiles`);
      setVoiceProfiles(vps);
    } catch (_) {
      setVoiceProfiles([]);
    }
  };
  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [workspaceId]);

  const remove = async (id) => {
    if (!window.confirm("Delete contact and their tracked posts?")) return;
    await apiDelete(`/workspaces/${workspaceId}/contacts/${id}`);
    toast.success("Removed");
    load();
  };

  return (
    <div>
      <div className="flex items-center justify-end gap-2 mb-5">
        <Dialog open={bulkOpen} onOpenChange={setBulkOpen}>
          <DialogTrigger asChild>
            <Button className="btn-ghost" data-testid="contacts-bulk-add-btn">
              <Upload className="w-3.5 h-3.5 mr-1.5" /> Bulk add
            </Button>
          </DialogTrigger>
          <BulkContactsDialog
            workspaceId={workspaceId}
            onClose={() => setBulkOpen(false)}
            onCreated={() => {
              setBulkOpen(false);
              load();
            }}
          />
        </Dialog>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button className="btn-primary" data-testid="contacts-add-btn">
              <Plus className="w-3.5 h-3.5 mr-1.5" /> Add contact
            </Button>
          </DialogTrigger>
          <NewContactDialog
            workspaceId={workspaceId}
            voiceProfiles={voiceProfiles}
            onClose={() => setOpen(false)}
            onCreated={() => {
              setOpen(false);
              load();
            }}
          />
        </Dialog>
      </div>
      {items.length === 0 ? (
        <div className="hairline px-12 py-16 text-center max-w-xl mx-auto fade-up">
          <div className="overline mb-3">empty</div>
          <h2 className="serif text-2xl text-parchment mb-2">No contacts yet.</h2>
          <p className="text-sm text-stone mb-6">
            Add prospects, peers, clients, big creators — anyone you want the engagement engine to watch.
          </p>
          <div className="flex items-center justify-center gap-2">
            <Button onClick={() => setBulkOpen(true)} className="btn-ghost" data-testid="contacts-empty-bulk-btn">
              <Upload className="w-3.5 h-3.5 mr-1.5" /> Bulk add via URLs
            </Button>
            <Button onClick={() => setOpen(true)} className="btn-primary" data-testid="contacts-empty-add-btn">
              Add single contact
            </Button>
          </div>
        </div>
      ) : (
        <div className="hairline overflow-hidden max-w-6xl">
          <div className="grid grid-cols-12 gap-3 px-5 py-3 hairline-b overline">
            <div className="col-span-4">contact</div>
            <div className="col-span-3">relationship</div>
            <div className="col-span-2">voice</div>
            <div className="col-span-2">icp</div>
            <div className="col-span-1"></div>
          </div>
          {items.map((c) => (
            <ContactRow
              key={c.id}
              workspaceId={workspaceId}
              contact={c}
              voiceProfiles={voiceProfiles}
              onChange={load}
              onDelete={() => remove(c.id)}
            />
          ))}
        </div>
      )}
    </div>
  );
}

function ContactRow({ workspaceId, contact, voiceProfiles, onChange, onDelete }) {
  const initials = (contact.name || "?").split(" ").map((w) => w[0]).slice(0, 2).join("").toUpperCase();

  const patch = async (changes) => {
    try {
      await apiPatch(`/workspaces/${workspaceId}/contacts/${contact.id}`, changes);
      onChange();
    } catch (e) {
      toast.error("Update failed");
    }
  };

  const customRel = !RELATIONSHIP_PRESETS.includes(contact.relationship);

  return (
    <div
      data-testid={`contact-row-${contact.id}`}
      className="grid grid-cols-12 gap-3 px-5 py-3 border-b border-zinc-800/60 text-sm items-center"
    >
      <div className="col-span-4 flex items-center gap-3 min-w-0">
        {contact.avatar_url ? (
          <img
            src={contact.avatar_url}
            alt={contact.name}
            className="w-9 h-9 rounded-full object-cover shrink-0"
          />
        ) : (
          <div className="w-9 h-9 rounded-full bg-zinc-800 flex items-center justify-center text-xs text-zinc-300 shrink-0">
            {initials}
          </div>
        )}
        <div className="min-w-0">
          <div className="flex items-center gap-2 min-w-0">
            <span className="text-zinc-100 truncate">{contact.name}</span>
            {contact.linkedin_url && (
              <a
                href={contact.linkedin_url}
                target="_blank"
                rel="noreferrer"
                className="text-zinc-500 hover:text-zinc-200 shrink-0"
                aria-label="Open"
              >
                <ExternalLink className="w-3 h-3" />
              </a>
            )}
          </div>
          {contact.headline && (
            <div className="text-xs text-zinc-500 truncate">{contact.headline}</div>
          )}
        </div>
      </div>
      <div className="col-span-3">
        <select
          data-testid={`rel-select-${contact.id}`}
          value={customRel ? "__custom" : contact.relationship}
          onChange={(e) => {
            if (e.target.value === "__custom") return;
            patch({ relationship: e.target.value });
          }}
          className="bg-zinc-900 border border-zinc-800 text-zinc-200 rounded-md px-2 py-1 text-xs w-full"
        >
          {RELATIONSHIP_PRESETS.map((r) => (
            <option key={r} value={r}>
              {r}
            </option>
          ))}
          {customRel && (
            <option value={contact.relationship}>{contact.relationship} (custom)</option>
          )}
          <option value="__custom">+ custom…</option>
        </select>
        {customRel && (
          <input
            data-testid={`rel-custom-${contact.id}`}
            defaultValue={contact.relationship}
            onBlur={(e) => {
              const v = e.target.value.trim();
              if (v && v !== contact.relationship) patch({ relationship: v });
            }}
            className="mt-1 bg-zinc-950 border border-zinc-800 text-zinc-200 rounded-md px-2 py-1 text-xs w-full"
          />
        )}
      </div>
      <div className="col-span-2">
        <select
          data-testid={`voice-select-${contact.id}`}
          value={contact.default_voice_profile_id || ""}
          onChange={(e) => patch({ default_voice_profile_id: e.target.value || null })}
          className="bg-zinc-900 border border-zinc-800 text-zinc-200 rounded-md px-2 py-1 text-xs w-full"
        >
          <option value="">workspace default</option>
          {voiceProfiles.map((vp) => (
            <option key={vp.id} value={vp.id}>
              {vp.name}
            </option>
          ))}
        </select>
      </div>
      <div className="col-span-2">
        <button
          onClick={() => patch({ icp_match: !contact.icp_match })}
          data-testid={`icp-toggle-${contact.id}`}
          className={`inline-flex items-center gap-1 text-xs px-2 py-1 rounded-sm border ${
            contact.icp_match
              ? "border-sky-400/50 text-sky-300 bg-sky-400/10"
              : "border-zinc-800 text-zinc-500 hover:text-zinc-300"
          }`}
        >
          <Sparkles className="w-3 h-3" /> {contact.icp_match ? "ICP" : "Not ICP"}
        </button>
      </div>
      <div className="col-span-1 text-right">
        <button
          onClick={onDelete}
          data-testid={`contact-delete-${contact.id}`}
          className="text-zinc-500 hover:text-red-400"
          aria-label="Delete"
        >
          <Trash2 className="w-3.5 h-3.5" />
        </button>
      </div>
    </div>
  );
}

function NewContactDialog({ workspaceId, voiceProfiles, onClose, onCreated }) {
  const [f, setF] = useState({
    name: "",
    linkedin_url: "",
    relationship: "prospect",
    icp_match: false,
    notes: "",
    default_voice_profile_id: "",
  });
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    if (!f.name.trim()) {
      toast.error("Name required");
      return;
    }
    setBusy(true);
    try {
      const payload = {
        ...f,
        default_voice_profile_id: f.default_voice_profile_id || null,
      };
      await apiPost(`/workspaces/${workspaceId}/contacts`, payload);
      toast.success("Contact added");
      onCreated();
    } catch (e) {
      toast.error("Failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <DialogContent className="bg-zinc-900 border-zinc-800 text-zinc-100 max-w-lg">
      <DialogHeader>
        <DialogTitle className="heading text-xl">New contact</DialogTitle>
      </DialogHeader>
      <div className="space-y-3 py-2">
        <div>
          <Label className="text-xs text-zinc-300 mb-1.5 block">Name *</Label>
          <Input
            data-testid="new-contact-name"
            value={f.name}
            onChange={(e) => setF({ ...f, name: e.target.value })}
            className="bg-zinc-950 border-zinc-800 text-zinc-100"
          />
        </div>
        <div>
          <Label className="text-xs text-zinc-300 mb-1.5 block">LinkedIn URL</Label>
          <Input
            data-testid="new-contact-linkedin"
            value={f.linkedin_url}
            onChange={(e) => setF({ ...f, linkedin_url: e.target.value })}
            placeholder="https://linkedin.com/in/…"
            className="bg-zinc-950 border-zinc-800 text-zinc-100"
          />
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <Label className="text-xs text-zinc-300 mb-1.5 block">Relationship</Label>
            <select
              data-testid="new-contact-rel"
              value={f.relationship}
              onChange={(e) => setF({ ...f, relationship: e.target.value })}
              className="w-full bg-zinc-950 border border-zinc-800 text-zinc-100 rounded-md px-3 py-2 text-sm"
            >
              {RELATIONSHIP_PRESETS.map((r) => (
                <option key={r} value={r}>
                  {r}
                </option>
              ))}
            </select>
          </div>
          <div>
            <Label className="text-xs text-zinc-300 mb-1.5 block">ICP match</Label>
            <button
              type="button"
              onClick={() => setF({ ...f, icp_match: !f.icp_match })}
              data-testid="new-contact-icp"
              className={`w-full text-sm px-3 py-2 rounded-md border ${
                f.icp_match
                  ? "border-sky-400/50 text-sky-300 bg-sky-400/10"
                  : "border-zinc-800 text-zinc-400"
              }`}
            >
              {f.icp_match ? "ICP match" : "Not ICP"}
            </button>
          </div>
        </div>
        {voiceProfiles?.length > 1 && (
          <div>
            <Label className="text-xs text-zinc-300 mb-1.5 block">Default voice</Label>
            <select
              data-testid="new-contact-voice"
              value={f.default_voice_profile_id}
              onChange={(e) => setF({ ...f, default_voice_profile_id: e.target.value })}
              className="w-full bg-zinc-950 border border-zinc-800 text-zinc-100 rounded-md px-3 py-2 text-sm"
            >
              <option value="">workspace default</option>
              {voiceProfiles.map((vp) => (
                <option key={vp.id} value={vp.id}>
                  {vp.name}
                </option>
              ))}
            </select>
          </div>
        )}
        <div>
          <Label className="text-xs text-zinc-300 mb-1.5 block">Notes</Label>
          <Textarea
            data-testid="new-contact-notes"
            rows={3}
            value={f.notes}
            onChange={(e) => setF({ ...f, notes: e.target.value })}
            placeholder="Why are they relevant?"
            className="bg-zinc-950 border-zinc-800 text-zinc-100"
          />
        </div>
        <div className="flex justify-end gap-2 pt-2">
          <button onClick={onClose} className="btn-ghost" data-testid="new-contact-cancel">
            Cancel
          </button>
          <Button onClick={submit} disabled={busy} className="btn-primary" data-testid="new-contact-submit">
            {busy ? "Adding…" : "Add contact"}
          </Button>
        </div>
      </div>
    </DialogContent>
  );
}

function BulkContactsDialog({ workspaceId, onClose, onCreated }) {
  const [urls, setUrls] = useState("");
  const [relationship, setRelationship] = useState("prospect");
  const [icpMatch, setIcpMatch] = useState(false);
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    const list = urls
      .split(/\s+/)
      .map((u) => u.trim())
      .filter((u) => u && u.includes("linkedin.com"));
    if (list.length === 0) {
      toast.error("Paste at least one LinkedIn URL");
      return;
    }
    setBusy(true);
    try {
      const r = await apiPost(`/workspaces/${workspaceId}/contacts/bulk`, {
        linkedin_urls: list,
        relationship,
        icp_match: icpMatch,
      });
      const msg = `${r.created?.length || 0} added${r.skipped?.length ? `, ${r.skipped.length} skipped` : ""}`;
      toast.success(msg);
      onCreated();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Bulk add failed");
    } finally {
      setBusy(false);
    }
  };

  const urlCount = urls.split(/\s+/).filter((u) => u.trim() && u.includes("linkedin.com")).length;

  return (
    <DialogContent className="bg-zinc-900 border-zinc-800 text-zinc-100 max-w-xl">
      <DialogHeader>
        <DialogTitle className="heading text-xl">Bulk add contacts</DialogTitle>
      </DialogHeader>
      <div className="space-y-4 py-2">
        <p className="text-sm text-zinc-400">
          Paste LinkedIn profile URLs (one per line). We'll auto-fetch each contact's name,
          headline, and headshot.
        </p>
        <div>
          <Label className="text-xs text-zinc-300 mb-1.5 block">
            LinkedIn URLs {urlCount > 0 && <span className="text-sky-400">· {urlCount} detected</span>}
          </Label>
          <Textarea
            data-testid="bulk-urls"
            rows={8}
            value={urls}
            onChange={(e) => setUrls(e.target.value)}
            placeholder={"https://www.linkedin.com/in/justinwelsh/\nhttps://www.linkedin.com/in/<another>/"}
            className="bg-zinc-950 border-zinc-800 text-zinc-100 font-mono text-xs"
          />
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <Label className="text-xs text-zinc-300 mb-1.5 block">Default relationship</Label>
            <select
              data-testid="bulk-rel"
              value={relationship}
              onChange={(e) => setRelationship(e.target.value)}
              className="w-full bg-zinc-950 border border-zinc-800 text-zinc-100 rounded-md px-3 py-2 text-sm"
            >
              {RELATIONSHIP_PRESETS.map((r) => (
                <option key={r} value={r}>
                  {r}
                </option>
              ))}
            </select>
          </div>
          <div>
            <Label className="text-xs text-zinc-300 mb-1.5 block">All ICP match?</Label>
            <button
              type="button"
              onClick={() => setIcpMatch(!icpMatch)}
              data-testid="bulk-icp"
              className={`w-full text-sm px-3 py-2 rounded-md border ${
                icpMatch
                  ? "border-sky-400/50 text-sky-300 bg-sky-400/10"
                  : "border-zinc-800 text-zinc-400"
              }`}
            >
              {icpMatch ? "Yes — all ICP" : "Mixed (set later)"}
            </button>
          </div>
        </div>
        <p className="text-xs text-zinc-500">
          Cost: ~$0.05 per contact for profile enrichment. {urlCount > 0 && `≈ $${(urlCount * 0.05).toFixed(2)}`}
        </p>
        <div className="flex justify-end gap-2 pt-2">
          <button onClick={onClose} className="btn-ghost" data-testid="bulk-cancel">
            Cancel
          </button>
          <Button onClick={submit} disabled={busy || urlCount === 0} className="btn-primary" data-testid="bulk-submit">
            {busy ? (
              <>
                <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" />
                Fetching {urlCount} profiles…
              </>
            ) : (
              <>
                <Upload className="w-3.5 h-3.5 mr-1.5" /> Add {urlCount || ""} contacts
              </>
            )}
          </Button>
        </div>
      </div>
    </DialogContent>
  );
}
